import {
  AfterContentInit,
  ChangeDetectorRef,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';

import { CheckboxFieldComponent } from '../checkbox-field/checkbox-field.component';
import { FilterPanelComponent } from '../filter-panel/filter-panel.component';
import { Column, ComputedColumn, FooterButton, HeaderButton, TableIcon } from '@interfaces';
import { RMS_TABLE_DIRECTIVES, RmsCellTemplateDirective } from './rms-table.directives';
import { RmsTableLayoutService } from './rms-table-layout.service';
import { FilterService } from '../../../services/filter-panel.service';
import { PromptService } from '../../../services/prompt.service';

export type Layout = {
  LayoutID?: number;
  LayoutName?: string;
  LayoutType?: 'Inventory' | 'Order' | 'Outbound' | null;
  UserName?: string;
  LayoutData?: string;
  SystemLayout?: boolean;
  LayoutDefaultedBy?: string;
  DefaultLayout?: boolean | null;
};

type FilterDefinition = {
  FilterType: string;
  value: any;
};

type FilterPredicate = (rowValue: any, row?: any) => boolean;

@Component({
  selector: 'rms-table',
  standalone: true,
  templateUrl: './rms-table.component.html',
  styleUrls: [
    './rms-table.component.scss',
    './rms-cell-template-styles.component.scss'
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RmsTableComponent),
      multi: true
    },
    DecimalPipe,
    CurrencyPipe
  ],
  imports: [
    CommonModule,
    MatMenuModule,
    FormsModule,
    MatPaginatorModule,
    MatIcon,
    MatButtonModule,
    CheckboxFieldComponent,
    RMS_TABLE_DIRECTIVES,
    FilterPanelComponent
  ]
})
export class RmsTableComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, AfterContentInit, OnDestroy {

  private _data: any[] = [];
  private _filteredDataCache: any[] = [];
  private _fdTimer: ReturnType<typeof setTimeout> | null = null;
  private _tplByKey = new Map<string, TemplateRef<any>>();
  private _widthSyncScheduled = false;
  private _originalColumns: Column[] = [];
  private initialized = false;
  private applyingLayout = false;
  private layoutPersistenceInitialized = false;
  private readonly destroy$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('tableEl') tableEl!: ElementRef<HTMLTableElement>;
  @ViewChild('headerFileInput') headerFileInput!: ElementRef<HTMLInputElement>;
  @ContentChildren(RmsCellTemplateDirective)
  private cellTemplates!: QueryList<RmsCellTemplateDirective>;

  @Input() parentContext: any = null;
  @Input() columns: Column[] = [];
  @Input() icons: TableIcon[] = [];
  @Input() footerButtons: FooterButton[] = [];
  @Input() footerRows: Record<string, string | number | null>[] = [];
  @Input() computedColumns: ComputedColumn[] = [];
  @Input() IncludePaginator = true;
  @Input() expandable = false;
  @Input() pageSize = 50;
  @Input() IncludeSearch = true;
  @Input() expandedTemplate: any;
  @Input() startSort = '';
  @Input() startSortDirection: 'asc' | 'desc' = 'desc';
  @Input() clickableRow = false;
  @Input() IncludeSort = true;
  @Input() ActionsText = 'Actions';
  @Input() ExpandedText = '';
  @Input() MultiExpand = false;
  @Input() headerBackground = '';
  @Input() startExpandedIndex: number | null = null;
  @Input() customizable = false;
  @Input() layoutType: 'Inventory' | 'Order' | 'Outbound' | null = null;
  @Input() tableId = '';
  @Input() HeaderButtons: HeaderButton[] | null = null;
  @Input() DragAndDrop = false;
  @Input() DragDropLabel = 'Drag/Drop file or click to select';
  @Input() DragAccept: string | null = null;
  @Input() DragMultiple = false;
  @Input() getIcons?: (row: any) => TableIcon[];
  @Input() onExpand?: (row: any) => void | Promise<void>;
  @Input() autoExpandFirst = false;
  @Input() autoExpandPredicate?: (row: any, index: number) => boolean;
  @Input() filterDebounceMs = 150;

  @Output() fileDropped = new EventEmitter<File | File[]>();
  @Output() layoutChange = new EventEmitter<Column[]>();
  @Output() fieldChanged = new EventEmitter<{ row: any; column: string; value: any }>();
  @Output() fieldClicked = new EventEmitter<{ row: any; column: string; value: any }>();
  @Output() expandRow = new EventEmitter<{ row: any }>();
  @Output() onRowClick = new EventEmitter<{ row: any }>();

  @Input() set data(value: any[]) {
    this._data = Array.isArray(value) ? value : [];
    this.populateDropdownSearchOptions();
    this.normalizeColumns();
    this.scheduleRecalcFiltered(true);
    this.scheduleSyncWidthsFromDOM();
  }

  get data(): any[] {
    return this._data;
  }

  showFilterPanel = false;
  draggingOverHeader = false;
  inlineFilter: Record<string, any> = {};
  panelFilter: Record<string, FilterPredicate> = {};
  sortColumn = '';
  sortDirection: 'asc' | 'desc' = 'desc';
  expandedRowIndex: number | null = null;
  currentPage = 0;
  allItemsChecked = false;
  isDraggingColumn = false;
  draggingVisibleIndex: number | null = null;
  selectedLayout: Layout = {};
  editTable = false;
  multiSelectOptions: { label: string; value: any }[] = [];
  DefaultLayout: Layout[] = [];
  CustomLayouts: Layout[] = [];
  SystemLayouts: Layout[] = [];
  loaded = false;

  private readonly MIN_COL_PCT = 10;
  private readonly MAX_COL_PCT = 400;
  private _resizeSession: {
    key: string;
    aIdx: number;
    startX: number;
    startWidth: number;
  } | null = null;

  onChange = (_: any): void => { };
  onTouched = (): void => { };

  constructor(
    private changeDetector: ChangeDetectorRef,
    private decimalPipe: DecimalPipe,
    private currencyPipe: CurrencyPipe,
    private layoutService: RmsTableLayoutService,
    private filterService: FilterService,
    private prompt: PromptService
  ) { }

  get effectiveTableId(): string {
    return this.tableId || this.layoutType || 'rms-table';
  }

  private get localLayoutKey(): string {
    return `${this.effectiveTableId}TableLayout`;
  }

  get hasIconsColumn(): boolean {
    return (this.icons?.length ?? 0) > 0 || typeof this.getIcons === 'function';
  }

  async ngOnInit(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    try {
      await this.waitForColumns();
    } catch (error) {
      console.error('RMS table initialization failed because columns were not available.', error);
      return;
    }

    this.captureOriginalColumns();
    this.validateColumnWidths();

    this.filterService.filters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {

        if (!payload) return;

        if (payload.tableId === this.effectiveTableId) {
          this.UpdateFilters(payload.filters ?? {});
        }
      });
    try {
      this.UpdateFilters(await this.filterService.initializeFilters(this.effectiveTableId));
    } catch (error) {
      console.error('Unable to load initial table filters.', error);
    }

    this.sortColumn = this.startSort;
    this.sortDirection = this.startSortDirection || 'desc';

    this.initializeLayoutPersistence();
    this.normalizeColumns();
    this.scheduleSyncWidthsFromDOM();

    if (this.customizable) {
      await this.reloadLayoutsAndApplyInitialLayout();
    }

    this.scheduleRecalcFiltered(true);
  }

  ngAfterViewInit(): void {
    this.scheduleSyncWidthsFromDOM();
  }

  ngAfterContentInit(): void {
    const rebuild = (): void => {
      this._tplByKey.clear();
      this.cellTemplates?.forEach(template => {
        if (template.key) this._tplByKey.set(template.key, template.tpl);
      });
    };

    rebuild();
    this.cellTemplates?.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(rebuild);
  }

  ngOnDestroy(): void {
    if (this._fdTimer) {
      clearTimeout(this._fdTimer);
      this._fdTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async waitForColumns(): Promise<void> {
    const checkInterval = 50;
    const timeout = 30000;
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const check = (): void => {
        if (this.columns?.length > 0) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error('Columns were not populated within the expected time.'));
        } else {
          setTimeout(check, checkInterval);
        }
      };
      check();
    });
  }

  private captureOriginalColumns(): void {
    if (!this._originalColumns.length) {
      this._originalColumns = this.cloneColumns(this.columns);
    }
  }

  private cloneColumns(columns: Column[] | null | undefined): Column[] {
    return (columns ?? []).map((column: any) => ({
      ...column,
      dropdownSearchOptions: Array.isArray(column.dropdownSearchOptions)
        ? [...column.dropdownSearchOptions]
        : column.dropdownSearchOptions
    }));
  }

  private serializeColumnsForLayout(columns: Column[]): any[] {
    return (columns ?? []).map((column: any, arrayIndex: number) => ({
      key: column.key,
      hidden: !!column.hidden,
      width: Number(column.width) || null,
      columnIndex: typeof column.columnIndex === 'number'
        ? column.columnIndex
        : arrayIndex
    }));
  }

  private initializeLayoutPersistence(): void {
    if (!this.customizable || this.layoutPersistenceInitialized) return;
    this.layoutPersistenceInitialized = true;

    this.layoutChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(columns => {
        if (this.applyingLayout) return;
        localStorage.setItem(
          this.localLayoutKey,
          JSON.stringify(this.serializeColumnsForLayout(columns))
        );
      });
  }

  private async reloadLayoutsAndApplyInitialLayout(): Promise<void> {
    await this.GetAllLayouts(false);

    const defaultLayout = this.DefaultLayout[0];
    if (defaultLayout?.LayoutData) {
      this.applyLayoutFromString(defaultLayout.LayoutData);
      this.selectedLayout = defaultLayout;
      return;
    }

    const savedLayout = localStorage.getItem(this.localLayoutKey);
    if (savedLayout) {
      this.applyLayoutFromString(savedLayout);
      return;
    }

    this.normalizeColumns();
  }

  private getCurrentUserName(): string {
    const email = localStorage.getItem('email') ?? '';
    return email.includes('@') ? email.split('@')[0] : email;
  }

  async GetAllLayouts(applyDefault = true): Promise<void> {
    const layouts: Layout[] = await this.layoutService.getLayouts(this.layoutType);
    const currentUser = this.getCurrentUserName().toLowerCase();

    const isUserDefault = (layout: Layout): boolean => {
      if (layout.DefaultLayout) return true;
      if (!layout.LayoutDefaultedBy || !currentUser) return false;
      return layout.LayoutDefaultedBy
        .split(',')
        .map(value => value.trim().toLowerCase())
        .includes(currentUser);
    };

    this.DefaultLayout = layouts.filter(isUserDefault);
    this.CustomLayouts = layouts.filter(layout => !layout.SystemLayout && !isUserDefault(layout));
    this.SystemLayouts = layouts.filter(layout => !!layout.SystemLayout);

    const defaultLayout = this.DefaultLayout[0];
    console.log('Default Layout:', defaultLayout);

    if (applyDefault && defaultLayout?.LayoutData) {
      this.applyLayoutFromString(defaultLayout.LayoutData);
      this.selectedLayout = defaultLayout;
    }
  }

  SelectLayout(layout: Layout): void {
    console.log('Selecting Layout:', layout);

    if (!layout?.LayoutData) {
      console.warn('The selected layout does not contain LayoutData.', layout);
      return;
    }

    this.selectedLayout = layout;
    this.applyLayoutFromString(layout.LayoutData);
    localStorage.setItem(this.localLayoutKey, layout.LayoutData);
    this.layoutService.setLayout(layout.LayoutData, this.layoutType);
  }

  private applyLayoutFromString(layoutJson: string): void {
    if (!layoutJson) return;
    this.applyingLayout = true;

    try {
      const parsed = JSON.parse(layoutJson);
      if (!Array.isArray(parsed)) {
        console.warn('LayoutData was not an array.', parsed);
        return;
      }

      const liveColumns = this.cloneColumns(
        this.columns?.length ? this.columns : this._originalColumns
      );
      const liveByKey = new Map<string, Column>();
      liveColumns.forEach(column => {
        if ((column as any).key) liveByKey.set((column as any).key, column);
      });

      const savedInOrder = parsed
        .map((column: any, index: number) => ({ column, index }))
        .sort((a, b) =>
          (typeof a.column?.columnIndex === 'number' ? a.column.columnIndex : a.index) -
          (typeof b.column?.columnIndex === 'number' ? b.column.columnIndex : b.index)
        )
        .map(item => item.column);

      const savedKeys = new Set<string>();
      const mergedColumns: Column[] = [];

      for (const savedColumn of savedInOrder) {
        const key = savedColumn?.key;
        if (!key || savedKeys.has(key)) continue;

        const liveColumn = liveByKey.get(key);
        if (!liveColumn) continue;

        savedKeys.add(key);
        mergedColumns.push({
          ...liveColumn,
          hidden: !!savedColumn.hidden,
          width: Number(savedColumn.width) > 0
            ? Number(savedColumn.width)
            : (liveColumn as any).width,
          columnIndex: mergedColumns.length
        } as Column);
      }

      for (const liveColumn of liveColumns) {
        const key = (liveColumn as any).key;
        if (!key || savedKeys.has(key)) continue;
        mergedColumns.push({
          ...liveColumn,
          hidden: true,
          columnIndex: mergedColumns.length
        } as Column);
      }

      if (!mergedColumns.length) {
        console.warn('The layout did not contain any currently valid columns.');
        return;
      }

      this.columns = mergedColumns;
      this.normalizeColumns();
      this.populateDropdownSearchOptions();
      this.scheduleSyncWidthsFromDOM();
      this.scheduleRecalcFiltered(true);
      console.log('Applied layout columns:', this.columns);
    } catch (error) {
      console.warn('Failed to parse table layout JSON.', error);
    } finally {
      this.applyingLayout = false;
    }
  }

  onInlineFilterChange(): void {
    this.currentPage = 0;
    this.scheduleRecalcFiltered();
  }

  clearInlineFilter(key: string): void {
    delete this.inlineFilter[key];
    this.inlineFilter = { ...this.inlineFilter };
    this.currentPage = 0;
    this.scheduleRecalcFiltered();
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.scheduleRecalcFiltered();
  }

  UpdateFilters(filters: Record<string, FilterDefinition>): void {

    if (!this.tableId)return

    const nextPanelFilters: Record<string, FilterPredicate> = {};

    for (const key of Object.keys(filters ?? {})) {
      const filter = filters[key];
      if (!filter?.FilterType) continue;

      const filterType = filter.FilterType;
      const filterValue = filter.value;

      switch (filterType) {
        case 'contains': {
          if (this.isEmptyFilterValue(filterValue)) break;
          const expected = String(filterValue).trim().toLowerCase();
          nextPanelFilters[key] = rowValue =>
            String(rowValue ?? '').toLowerCase().includes(expected);
          break;
        }
        case 'notContains': {
          if (this.isEmptyFilterValue(filterValue)) break;
          const expected = String(filterValue).trim().toLowerCase();
          nextPanelFilters[key] = rowValue =>
            !String(rowValue ?? '').toLowerCase().includes(expected);
          break;
        }
        case '=': {
          if (this.isEmptyFilterValue(filterValue)) break;
          const expected = String(filterValue).trim().toLowerCase();
          nextPanelFilters[key] = rowValue =>
            String(rowValue ?? '').trim().toLowerCase() === expected;
          break;
        }
        case '!=': {
          if (this.isEmptyFilterValue(filterValue)) break;
          const expected = String(filterValue).trim().toLowerCase();
          nextPanelFilters[key] = rowValue =>
            String(rowValue ?? '').trim().toLowerCase() !== expected;
          break;
        }
        case '>': {
          if (this.isEmptyFilterValue(filterValue)) break;
          const expected = Number(filterValue);
          if (!Number.isFinite(expected)) break;
          nextPanelFilters[key] = rowValue => {
            const actual = Number(rowValue);
            return Number.isFinite(actual) && actual > expected;
          };
          break;
        }
        case '<': {
          if (this.isEmptyFilterValue(filterValue)) break;
          const expected = Number(filterValue);
          if (!Number.isFinite(expected)) break;
          nextPanelFilters[key] = rowValue => {
            const actual = Number(rowValue);
            return Number.isFinite(actual) && actual < expected;
          };
          break;
        }
        case 'Between': {
          const betweenValue = filterValue ?? {};
          const hasDateRange =
            !this.isEmptyFilterValue(betweenValue.StartDate) &&
            !this.isEmptyFilterValue(betweenValue.EndDate);
          const hasNumberRange =
            !this.isEmptyFilterValue(betweenValue.Start) &&
            !this.isEmptyFilterValue(betweenValue.End);

          if (hasDateRange) {
            const startDate = this.toValidDate(betweenValue.StartDate);
            const endDate = this.toValidDate(betweenValue.EndDate);
            if (!startDate || !endDate) break;
            endDate.setHours(23, 59, 59, 999);
            nextPanelFilters[key] = rowValue => {
              const actualDate = this.toValidDate(rowValue);
              return !!actualDate && actualDate >= startDate && actualDate <= endDate;
            };
          } else if (hasNumberRange) {
            const startNumber = Number(betweenValue.Start);
            const endNumber = Number(betweenValue.End);
            if (!Number.isFinite(startNumber) || !Number.isFinite(endNumber)) break;
            nextPanelFilters[key] = rowValue => {
              const actualNumber = Number(rowValue);
              return Number.isFinite(actualNumber) &&
                actualNumber >= startNumber && actualNumber <= endNumber;
            };
          }
          break;
        }
        case 'Empty': {
          nextPanelFilters[key] = rowValue =>
            rowValue == null || (typeof rowValue === 'string' && rowValue.trim() === '');
          break;
        }
        case 'Not Empty': {
          nextPanelFilters[key] = rowValue =>
            rowValue != null && (typeof rowValue !== 'string' || rowValue.trim() !== '');
          break;
        }
        default:
          console.warn(`Unsupported RMS filter type "${filterType}" for column "${key}".`);
      }
    }

    this.panelFilter = nextPanelFilters;
    this.currentPage = 0;
    this.scheduleRecalcFiltered(true);
  }

  private isEmptyFilterValue(value: any): boolean {
    return value === null || value === undefined ||
      (typeof value === 'string' && value.trim() === '');
  }

  private toValidDate(value: any): Date | null {
    if (this.isEmptyFilterValue(value)) return null;
    const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private scheduleRecalcFiltered(immediate = false): void {
    if (this._fdTimer) clearTimeout(this._fdTimer);
    const delay = immediate ? 0 : Math.max(0, this.filterDebounceMs | 0);
    this._fdTimer = setTimeout(() => {
      this._fdTimer = null;
      this._recomputeFiltered();
    }, delay);
  }

  private _recomputeFiltered(): void {
    const source = Array.isArray(this.data) ? this.data : [];
    const allFilters: Record<string, FilterPredicate> = { ...this.panelFilter };

    for (const key of Object.keys(this.inlineFilter ?? {})) {
      const selectedValue = this.inlineFilter[key];
      if (this.isEmptyFilterValue(selectedValue)) continue;

      const column = this.columns.find(candidate => (candidate as any).key === key);
      if ((column as any)?.SearchType === 'checkbox') {
        allFilters[key] = rowValue => {
          const isChecked = rowValue === true || rowValue === 1 || rowValue === '1' ||
            String(rowValue).toLowerCase() === 'true';
          if (selectedValue === 'checked') return isChecked;
          if (selectedValue === 'unchecked') return !isChecked;
          return true;
        };
      } else if ((column as any)?.SearchType === 'select') {
        // Dropdown options are exact, discrete values (e.g. "Active" vs "Inactive") -
        // substring matching here would let "Active" match "Inactive" rows too.
        const expected = String(selectedValue).trim().toLowerCase();
        allFilters[key] = rowValue =>
          String(rowValue ?? '').toLowerCase() === expected;
      } else {
        const expected = String(selectedValue).trim().toLowerCase();
        allFilters[key] = rowValue =>
          String(rowValue ?? '').toLowerCase().includes(expected);
      }
    }

    const activeFilterEntries = Object.entries(allFilters);
    const output = source
      .filter(row => activeFilterEntries.every(([key, predicate]) =>
        predicate(this.getRowValue(row, key), row)
      ))
      .sort((a, b) => this.compareRows(a, b));

    this._filteredDataCache = output;
    // Deferred filtering must also refresh tables inside OnPush overlays.
    this.changeDetector.markForCheck();

    if (this.IncludePaginator) {
      const maxPage = Math.max(0, Math.ceil(output.length / this.pageSize) - 1);
      if (this.currentPage > maxPage) this.currentPage = maxPage;
    } else {
      this.currentPage = 0;
    }

    this.applyAutoExpand();
  }

  private getRowValue(row: any, key: string): any {
    if (!row || !key) return undefined;
    return key.split('.').reduce(
      (value: any, segment: string) => value == null ? undefined : value[segment],
      row
    );
  }

  private compareRows(a: any, b: any): number {
    if (!this.IncludeSort || !this.sortColumn) return 0;
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    const aValue = this.getRowValue(a, this.sortColumn);
    const bValue = this.getRowValue(b, this.sortColumn);

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1 * direction;
    if (bValue == null) return 1 * direction;

    const aNumber = this.toComparableNumber(aValue);
    const bNumber = this.toComparableNumber(bValue);
    if (aNumber !== null && bNumber !== null) return (aNumber - bNumber) * direction;

    return String(aValue).localeCompare(String(bValue), undefined, {
      numeric: true,
      sensitivity: 'base'
    }) * direction;
  }

  private toComparableNumber(value: any): number | null {
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    return null;
  }

  get filteredData(): any[] {
    return this._filteredDataCache;
  }

  get paginatedData(): any[] {
    if (!this.IncludePaginator) return this.filteredData;
    const startIndex = this.currentPage * this.pageSize;
    return this.filteredData.slice(startIndex, startIndex + this.pageSize);
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    if (this.MultiExpand) {
      if (!this.paginatedData.some(row => row?.expanded)) this.applyAutoExpand();
    } else if (this.expandedRowIndex === null) {
      this.applyAutoExpand();
    }
  }

  onSortKeydown(event: KeyboardEvent, key: string): void {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    this.setSort(key);
  }

  setSort(key: string): void {
    if (!this.IncludeSort || this.columns?.find(column => column.key === key)?.removeSort) return;
    if (this.sortColumn === key) {
      if (this.sortDirection === 'asc') this.sortDirection = 'desc';
      else { this.sortColumn = ''; this.sortDirection = 'asc'; }
    } else {
      this.sortColumn = key;
      this.sortDirection = 'asc';
    }
    this.scheduleRecalcFiltered();
  }

  private populateDropdownSearchOptions(): void {
    const nextInlineFilter = { ...this.inlineFilter };

    for (const column of this.columns ?? []) {
      if (column.SearchType !== 'select' || !column.key) continue;
      // A caller-provided list (e.g. a fixed Yes/No for a status column) should win over
      // deriving options from whatever rows happen to be loaded right now - otherwise a
      // value with zero current matches (like "Yes" when every loaded row is "No") would
      // silently disappear from the dropdown instead of just returning no results.
      if (Array.isArray(column.dropdownSearchOptions) && column.dropdownSearchOptions.length) continue;

      const uniqueValues = Array.from(new Set(
        this._data
          .map(row => this.getRowValue(row, column.key))
          .filter(value => value !== null && value !== undefined && value !== '')
      )).sort((a, b) => String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base'
      }));

      column.dropdownSearchOptions = uniqueValues.map(value => ({
        value,
        label: String(value)
      }));

      if (!Object.prototype.hasOwnProperty.call(nextInlineFilter, column.key)) {
        nextInlineFilter[column.key] = '';
      }
    }

    this.inlineFilter = nextInlineFilter;
  }

  private ensureColumnIndices(): void {
    let next = 0;
    for (const column of this.columns ?? []) {
      if (typeof (column as any).columnIndex !== 'number') {
        (column as any).columnIndex = next++;
      } else {
        next = Math.max(next, (column as any).columnIndex + 1);
      }
    }
  }

  private seedAutoWidthsIfMissing(): void {
    const visible = (this.columns ?? []).filter((column: any) => !column.hidden);
    if (!visible.length) return;
    const missing = visible.filter((column: any) => !(column.width > 0));
    if (!missing.length) return;
    const defaultPct = Math.max(5, Math.floor(100 / visible.length));
    missing.forEach((column: any) => column.width = defaultPct);
  }

  private normalizeColumns(): void {
    this.ensureColumnIndices();
    this.seedAutoWidthsIfMissing();
    this.multiSelectOptions = this.columns.map(column => ({
      label: this.getColumnLabel(column),
      value: !(column as any).hidden
    }));
  }

  private reindexVisibleColumns(): void {
    let visibleIndex = 0;
    this.columns = this.columns.map(column => (column as any).hidden
      ? { ...column }
      : { ...column, columnIndex: visibleIndex++ }
    );
  }

  onColumnsChanged(options: any[]): void {
    if (!Array.isArray(options)) return;
    this.columns = this.columns.map((column, index) => ({
      ...column,
      hidden: !options[index]?.value
    }));
    this.reindexVisibleColumns();
    this.normalizeColumns();
    this.layoutChange.emit(this.cloneColumns(this.columns));
    this.scheduleSyncWidthsFromDOM();
  }

  clearLayout(): void {
    const sourceColumns = this._originalColumns.length ? this._originalColumns : this.columns;
    this.applyingLayout = true;
    try {
      this.columns = this.cloneColumns(sourceColumns);
      this.selectedLayout = {};
      localStorage.removeItem(this.localLayoutKey);
      this.normalizeColumns();
      this.populateDropdownSearchOptions();
      this.scheduleSyncWidthsFromDOM();
      this.scheduleRecalcFiltered(true);
    } finally {
      this.applyingLayout = false;
    }
    this.layoutChange.emit(this.cloneColumns(this.columns));
  }

  private scheduleSyncWidthsFromDOM(): void {
    if (this._widthSyncScheduled) return;
    this._widthSyncScheduled = true;
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.syncWidthsFromDOMIfMissing();
        this._widthSyncScheduled = false;
      }, 0);
    });
  }

  private syncWidthsFromDOMIfMissing(): void {
    try {
      const table = this.tableEl?.nativeElement;
      if (!table) return;
      const visible = this.visibleColumns;
      if (!visible.some((column: any) => !(column.width > 0))) return;

      const headerRows = Array.from(table.querySelectorAll('thead tr'));
      if (!headerRows.length) return;
      const labelRow = headerRows[this.customizable ? 1 : 0] ?? headerRows[0];
      const tableClientWidth = Math.max(1, table.clientWidth);
      const thByKey = new Map<string, HTMLElement>();

      Array.from(labelRow.querySelectorAll('th')).forEach(th => {
        const key = (th as HTMLElement).getAttribute('data-col-key');
        if (key) thByKey.set(key, th as HTMLElement);
      });

      let wroteAny = false;
      for (const column of visible as any[]) {
        if (!(column.width > 0)) {
          const px = thByKey.get(column.key)?.offsetWidth || 0;
          if (px > 0) {
            column.width = Math.max(5, Math.round((px / tableClientWidth) * 10000) / 100);
            wroteAny = true;
          }
        }
      }

      if (wroteAny) {
        this.normalizeColumns();
        this.layoutChange.emit(this.cloneColumns(this.columns));
      }
    } catch {
      // Measuring can fail while the table is detached from the DOM.
    }
  }

  onColDragBegin = (event: { visibleIndex: number }): void => {
    this.isDraggingColumn = true;
    this.draggingVisibleIndex = event.visibleIndex;
  };

  onColDragEnd = (_: { visibleIndex: number }): void => {
    this.isDraggingColumn = false;
    this.draggingVisibleIndex = null;
  };

  private clampPct(value: number): number {
    const normalized = Number.isFinite(+value) ? +value : 10;
    return Math.min(this.MAX_COL_PCT, Math.max(this.MIN_COL_PCT,
      Math.round(normalized * 100) / 100));
  }

  onColumnResizeStart = (event: { key: string; clientX: number }): void => {
    this.normalizeColumns();
    const aIdx = this.columns.findIndex(column => (column as any).key === event.key);
    if (aIdx < 0) return;
    const startWidth = Number((this.columns[aIdx] as any).width) || 10;
    this._resizeSession = { key: event.key, aIdx, startX: event.clientX, startWidth };
  };

  onColumnResizeMove = (event: { key: string; clientX: number }): void => {
    const session = this._resizeSession;
    if (!session || session.key !== event.key) return;

    const tableWidth = Math.max(1, this.tableEl?.nativeElement?.clientWidth ?? 1000);
    const deltaPct = ((event.clientX - session.startX) / tableWidth) * 100;
    const nextWidth = this.clampPct(session.startWidth + deltaPct);

    this.columns = this.columns.map((column, index) =>
      index === session.aIdx ? { ...column, width: nextWidth } : column
    );
  };

  onColumnResizeEnd = (_: { key: string }): void => {
    this.normalizeColumns();
    this.layoutChange.emit(this.cloneColumns(this.columns));
    this.scheduleSyncWidthsFromDOM();
    this._resizeSession = null;
  };

  onColumnReorder(event: { fromIndex: number; toIndex: number }): void {
    if (!event) return;
    const visible = this.visibleColumns;
    const fromColumn = visible[event.fromIndex];
    const toVisibleIndex = event.toIndex;
    if (!fromColumn || toVisibleIndex < 0 || toVisibleIndex > visible.length) return;

    const columns = this.cloneColumns(this.columns);
    const absoluteFrom = columns.findIndex(column =>
      (column as any).key === (fromColumn as any).key);
    if (absoluteFrom < 0) return;

    const visibleAbsoluteIndexes: number[] = [];
    columns.forEach((column, index) => {
      if (!(column as any).hidden) visibleAbsoluteIndexes.push(index);
    });

    let absoluteInsert: number;
    if (toVisibleIndex === 0) {
      absoluteInsert = visibleAbsoluteIndexes[0] ?? 0;
    } else if (toVisibleIndex === visibleAbsoluteIndexes.length) {
      absoluteInsert = (visibleAbsoluteIndexes[visibleAbsoluteIndexes.length - 1]
        ?? columns.length - 1) + 1;
    } else {
      absoluteInsert = visibleAbsoluteIndexes[toVisibleIndex];
    }

    const [moving] = columns.splice(absoluteFrom, 1);
    if (absoluteFrom < absoluteInsert) absoluteInsert--;
    columns.splice(absoluteInsert, 0, moving);
    this.columns = columns;
    this.reindexVisibleColumns();
    this.normalizeColumns();
    this.layoutChange.emit(this.cloneColumns(this.columns));
    this.scheduleSyncWidthsFromDOM();
  }

  onColumnHide(event: { key?: string; index?: number }): void {
    let key = event.key;
    if (!key && typeof event.index === 'number') {
      key = (this.visibleColumns[event.index] as any)?.key;
    }
    if (!key) return;

    this.columns = this.columns.map(column =>
      (column as any).key === key ? { ...column, hidden: true } : { ...column }
    );
    this.reindexVisibleColumns();
    this.normalizeColumns();
    this.layoutChange.emit(this.cloneColumns(this.columns));
    this.scheduleSyncWidthsFromDOM();
  }

  unhideColumn(column: Column): void {
    const key = (column as any).key;
    this.columns = this.columns.map(current =>
      (current as any).key === key ? { ...current, hidden: false } : { ...current }
    );
    this.reindexVisibleColumns();
    this.normalizeColumns();
    this.layoutChange.emit(this.cloneColumns(this.columns));
    this.scheduleSyncWidthsFromDOM();
  }

  get visibleColumns(): Column[] {
    return (this.columns ?? [])
      .filter((column: any) => !column.hidden)
      .slice()
      .sort((a: any, b: any) => (a.columnIndex ?? 0) - (b.columnIndex ?? 0));
  }

  get removedColumns(): Column[] {
    return (this.columns ?? [])
      .filter((column: any) => !!column.hidden)
      .slice()
      .sort((a: any, b: any) => (a.columnIndex ?? 0) - (b.columnIndex ?? 0));
  }

  get filterColumns(): Column[] {
    return this.columns.filter((column: any) => !column.hidden);
  }

  get totalTableWidth(): number {
    if (!this.customizable) return 100;
    const base = this.visibleColumns.reduce(
      (sum, column: any) => sum + (Number(column.width) || 0), 0);
    const icons = this.hasIconsColumn ? 5 : 0;
    const expand = (this.expandable || this.hasExpandableRow()) ? 5 : 0;
    return Math.max(100, Math.round((base + icons + expand) * 100) / 100);
  }

  private validateColumnWidths(): void {
    const totalWidth = this.columns.reduce(
      (sum, column: any) => sum + (column.width ?? 0), 0) +
      (this.hasIconsColumn ? 5 : 0) +
      (this.expandable ? 5 : 0);

    if (totalWidth > 100) {
      console.warn(`Column widths exceed 100 (Total: ${totalWidth})`);
    } else if (totalWidth < 100) {
      console.warn(`Column widths are less than 100 (Total: ${totalWidth})`);
    }
  }

  async onSaveNewLayout(): Promise<void> {
    const { closed$ } = this.prompt.open({
      promptText: 'Input a name for your new table view.',
      buttons: [{ label: 'Save', value: true }],
      promptInput: 'text',
      inputRequired: true,
      inputLabel: 'Table View Name'
    });

    closed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        const name = result?.inputValue?.trim();
        if (name) void this.SaveLayoutAsNew(name);
      });
  }

  async SaveLayoutAsNew(name: string): Promise<void> {
    const { closed$ } = this.prompt.open({
      promptText: 'Set as default layout?',
      buttons: [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ]
    });

    closed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async result => {
        if (result === null) return;

        const layoutData = JSON.stringify(this.serializeColumnsForLayout(this.columns));
        const layoutBody = {
          LayoutName: name,
          LayoutType: this.layoutType,
          LayoutData: layoutData,
          DefaultLayout: !!result,
          UserName: this.getCurrentUserName()
        };

        await this.layoutService.saveLayout(JSON.stringify(layoutBody));
        localStorage.setItem(this.localLayoutKey, layoutData);
        await this.GetAllLayouts(false);
        this.selectedLayout = this.findMatchingLayout(name) ?? this.selectedLayout;
      });
  }

  async UpdateLayout(): Promise<void> {
    console.log('Updating Layout:', this.selectedLayout);
    if (!this.selectedLayout?.LayoutID) {
      console.warn('A saved layout must be selected before Update can be used.');
      return;
    }

    const selectedLayoutId = this.selectedLayout.LayoutID;
    const layoutData = JSON.stringify(this.serializeColumnsForLayout(this.columns));
    const layoutBody = {
      LayoutID: selectedLayoutId,
      LayoutName: this.selectedLayout.LayoutName,
      LayoutType: this.layoutType,
      LayoutData: layoutData,
      DefaultLayout: this.selectedLayout.DefaultLayout ?? false,
      UserName: this.getCurrentUserName()
    };

    await this.layoutService.saveLayout(JSON.stringify(layoutBody));
    localStorage.setItem(this.localLayoutKey, layoutData);
    await this.GetAllLayouts(false);

    const refreshedLayout = [
      ...this.DefaultLayout,
      ...this.CustomLayouts,
      ...this.SystemLayouts
    ].find(layout => layout.LayoutID === selectedLayoutId);

    if (refreshedLayout) this.selectedLayout = refreshedLayout;
  }

  private findMatchingLayout(layoutName: string): Layout | undefined {
    return [
      ...this.DefaultLayout,
      ...this.CustomLayouts,
      ...this.SystemLayouts
    ].find(layout =>
      layout.LayoutName === layoutName && layout.LayoutType === this.layoutType
    );
  }

  writeValue(value: any): void {
    this._data = Array.isArray(value) ? value : [];
    this.populateDropdownSearchOptions();
    this.scheduleRecalcFiltered(true);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  toggleExpand(index: number, row?: any): void {
    const visible = this.paginatedData;
    const target = row ?? visible[index];
    if (!target) return;

    if (this.MultiExpand) {
      target.expanded = !target.expanded;
      return;
    }

    if (this.isExpanded(index)) {
      target.expanded = false;
      this.expandedRowIndex = null;
      return;
    }

    visible.forEach(item => item.expanded = false);
    target.expanded = true;
    this.expandedRowIndex = index;
  }

  handleExpand(row: any): void {
    this.expandRow.emit({ row });
  }

  isExpanded(index: number): boolean {
    const row = this.paginatedData[index];
    return this.MultiExpand
      ? !!row?.expanded
      : this.expandedRowIndex === index || !!row?.expanded;
  }

  private applyAutoExpand(): void {
    if (!this.expandable) return;
    const visible = this.paginatedData;

    if (this.MultiExpand) {
      if (visible.some(row => row?.expanded)) return;
      if (this.startExpandedIndex != null &&
        this.startExpandedIndex >= 0 &&
        this.startExpandedIndex < visible.length) {
        visible[this.startExpandedIndex].expanded = true;
        return;
      }
      if (this.autoExpandPredicate) {
        const index = visible.findIndex((row, i) => this.autoExpandPredicate!(row, i));
        if (index >= 0) {
          visible[index].expanded = true;
          return;
        }
      }
      if (this.autoExpandFirst && visible.length) visible[0].expanded = true;
      return;
    }

    if (this.expandedRowIndex !== null) return;
    const presetIndex = visible.findIndex(row => row?.expanded === true);
    if (presetIndex >= 0) {
      this.expandedRowIndex = presetIndex;
      return;
    }
    if (this.startExpandedIndex != null &&
      this.startExpandedIndex >= 0 &&
      this.startExpandedIndex < visible.length) {
      this.expandedRowIndex = this.startExpandedIndex;
      return;
    }
    if (this.autoExpandPredicate) {
      const index = visible.findIndex((row, i) => this.autoExpandPredicate!(row, i));
      if (index >= 0) {
        this.expandedRowIndex = index;
        return;
      }
    }
    if (this.autoExpandFirst && visible.length) this.expandedRowIndex = 0;
  }

  isComputedColumn(columnKey: string): boolean {
    return this.computedColumns?.some(column => column.key === columnKey) ?? false;
  }

  computeValue(columnKey: string, row: any): number | string {
    const computedColumn = this.computedColumns?.find(column => column.key === columnKey);
    return computedColumn ? computedColumn.formula(row).toFixed(2) : '';
  }

  getColumnMin(column: any, row: any): number | string | null {
    return typeof column.min === 'function' ? column.min(row) : column.min;
  }

  getColumnMax(column: any, row: any): number | string | null {
    return typeof column.max === 'function' ? column.max(row) : column.max;
  }

  formatValue(value: any, column: any): string {
    if (value == null || value === '') return '';
    const decimalFormat = column.decimal || '1.2-2';
    switch (column.pipe) {
      case 'currency':
        return this.currencyPipe.transform(value, 'USD', 'symbol', decimalFormat) || '';
      case 'number':
        return this.decimalPipe.transform(value, decimalFormat) || '';
      default:
        return String(value);
    }
  }

  onFixed(number: number, column: any): string {
    if (!number && number !== 0) return '--';
    return column.fixed ? number.toFixed(column.fixed) : number.toString();
  }

  getColumnLabel(column: any): string {
    if (!column.labelKey) return column.label;
    return this.data.length > 0 ? this.data[0][column.labelKey] : '';
  }

  getCellTemplate(key: string): TemplateRef<any> | null {
    return this._tplByKey.get(key) ?? null;
  }

  hasExpandableRow(): boolean {
    return (this.data ?? []).some(row => row?.expandable);
  }

  setDateValue(row: any, key: string, value: string): void {
    row[key] = value ? new Date(value) : null;
  }

  getDateValue(row: any, key: string): string | null {
    const value = row?.[key];
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  shouldHostWatermark(columnIndex: number, column: any, row: any): boolean {
    const highlight = row?.rowHighlight;
    if (!highlight?.text) return false;
    const scope = highlight.scope ?? 'row';
    if (scope === 'row') return columnIndex === 0;
    if (scope === 'cell') {
      if (!highlight.columnKey) return false;
      const keys = Array.isArray(highlight.columnKey)
        ? highlight.columnKey
        : [highlight.columnKey];
      return keys.includes(column?.key);
    }
    return false;
  }

  hasCellValue(column: Column, row: any): boolean {
    const value = this.isComputedColumn(column.key) ? this.computeValue(column.key, row) : row?.[column.key];
    if (value == null) return false;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'string') return !['', '-', '--', '—', '–'].includes(value.trim());
    return true;
  }

  onClickCell(column: Column, row: any): void {
    if (this.hasCellValue(column, row)) column.onAction?.(row);
  }

  onClickRow(row: any): void {
    if (this.clickableRow) this.onRowClick.emit({ row });
  }

  allFilteredChecked(column: Column): boolean {
    return this.filteredData.length > 0 && this.filteredData.every(row => !!row[column.key]);
  }

  someFilteredChecked(column: Column): boolean {
    return this.filteredData.some(row => !!row[column.key]) && !this.allFilteredChecked(column);
  }

  toggleFilteredColumn(column: Column, checked: boolean): void {
    if (!column.edit) return;
    for (const row of [...this.filteredData]) {
      row[column.key] = checked;
      column.onAction?.(row);
    }
  }

  OnCheckUncheckAll(): void {
    this.allItemsChecked = !this.allItemsChecked;
    const checkboxColumns = (this.columns ?? []).filter((column: any) =>
      column.type === 'checkbox');
    if (!checkboxColumns.length || !Array.isArray(this.data)) return;

    for (const row of this.data) {
      for (const column of checkboxColumns) {
        if ((column as any).edit) row[(column as any).key] = this.allItemsChecked;
      }
    }
  }

  onHeaderDragOver(event: DragEvent): void {
    if (!this.DragAndDrop) return;
    event.preventDefault();
    event.stopPropagation();
    this.draggingOverHeader = true;
  }

  onHeaderDragLeave(event?: DragEvent): void {
    if (!this.DragAndDrop) return;
    event?.preventDefault();
    event?.stopPropagation();
    this.draggingOverHeader = false;
  }

  onHeaderDrop(event: DragEvent): void {
    if (!this.DragAndDrop) return;
    event.preventDefault();
    event.stopPropagation();
    this.draggingOverHeader = false;

    const files = event.dataTransfer?.files
      ? Array.from(event.dataTransfer.files)
      : [];
    if (!files.length) return;
    this.fileDropped.emit(this.DragMultiple ? files : files[0]);
  }

  openHeaderFileDialog(): void {
    if (this.DragAndDrop) this.headerFileInput?.nativeElement?.click();
  }

  onHeaderBrowseChange(event: Event): void {
    if (!this.DragAndDrop) return;
    const input = event.target as HTMLInputElement;
    const files = input?.files ? Array.from(input.files) : [];
    if (!files.length) return;
    this.fileDropped.emit(this.DragMultiple ? files : files[0]);
    input.value = '';
  }

  getCellClass(column: any, row: any): string {
    if (!column?.cellClass) return '';
    return typeof column.cellClass === 'function'
      ? column.cellClass(row) ?? ''
      : column.cellClass;
  }
}
